// Shared constants for the parallel operation of this (legacy) management
// frontend and the new management frontend (new.management.fun-zone.de).
// The usage of both frontends is tracked via /api/v1/frontendUsage so the
// migration can be steered with data.

export const CURRENT_FRONTEND = 'legacy';

export const NEW_FRONTEND_BASE_URL = (
  import.meta.env.VITE_NEW_MANAGEMENT_URL || 'https://new.management.fun-zone.de'
).replace(/\/$/, '');

// Permanent address of this legacy frontend. From the switch-over date on,
// management.fun-zone.de serves the new dashboard, so this app is only
// reachable under its own hostname.
export const LEGACY_FRONTEND_BASE_URL = (
  import.meta.env.VITE_OLD_MANAGEMENT_URL || 'https://old.management.fun-zone.de'
).replace(/\/$/, '');

// Date on which management.fun-zone.de starts serving the new dashboard.
export const MANAGEMENT_DOMAIN_SWITCH_DATE = '15. September 2026';

// Hostname the switch-over applies to — the address staff have bookmarked.
export const SHARED_MANAGEMENT_HOSTNAME = 'management.fun-zone.de';

// The same address as a URL. Since the switch-over it serves the new dashboard,
// so this is where users of this app are sent — not the new dashboard's own
// hostname, because this is the address they are meant to keep.
export const SHARED_MANAGEMENT_URL = `https://${SHARED_MANAGEMENT_HOSTNAME}`;

// Day this legacy frontend is switched off for good. ISO date for arithmetic,
// spelled out for the copy — both describe the same day.
export const LEGACY_FRONTEND_SHUTDOWN_DATE = '2026-11-01';
export const LEGACY_FRONTEND_SHUTDOWN_LABEL = '1. November 2026';

// Routes on which no tracking/popups should happen (not logged in).
export const PUBLIC_PATHS = new Set([
  '/',
  '/login',
  '/rfid-login',
  '/request-password-reset',
  '/reset-password',
]);

function normalizePath(pathname) {
  if (!pathname) return '/';
  const trimmed = String(pathname).trim();
  if (!trimmed) return '/';
  if (trimmed.length > 1 && trimmed.endsWith('/')) return trimmed.slice(0, -1);
  return trimmed;
}

// URL of the equivalent page in the new frontend. The rebuild ports every
// route 1:1 (see funzone-management-frontend-new src/config/menu.ts), so the
// current pathname carries over directly; the root falls back to the
// dashboard.
export function buildNewFrontendUrl(pathname) {
  const normalized = normalizePath(pathname);
  return NEW_FRONTEND_BASE_URL + (normalized === '/' ? '/welcome' : normalized);
}

// The equivalent page under the shared management address, which serves the new
// dashboard. Same 1:1 route mapping as buildNewFrontendUrl.
export function buildSharedManagementUrl(pathname) {
  const normalized = normalizePath(pathname);
  return SHARED_MANAGEMENT_URL + (normalized === '/' ? '/welcome' : normalized);
}

// Whole days left until the shutdown, counted in calendar days of the local
// (German) timezone: 1 on the day before, 0 on the day itself, negative after.
// Both ends are local midnights, so the two DST days do not shift the result.
export function daysUntilLegacyShutdown(now = new Date()) {
  const [year, month, day] = LEGACY_FRONTEND_SHUTDOWN_DATE.split('-').map(Number);
  const shutdown = new Date(year, month - 1, day);
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((shutdown - today) / (24 * 60 * 60 * 1000));
}
